import React, { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import * as monaco from "monaco-editor";
import { createWsTicket, getWsBaseUrl } from "../../api/wsTicketApi";

// ── codingLanguage → Monaco / LSP mapping ───────────────────────────
// monacoId:   language ID passed to Monaco's <Editor language> prop
// lspKey:     value for the `language` query-param on the WS endpoint
// lspLangId:  value sent in textDocument/didOpen → languageId
// ext:        virtual file extension for the model URI
const LSP_LANG_CONFIG = {
  python:     { monacoId: "python",     lspKey: "python",     lspLangId: "python",          ext: "py"   },
  fastapi:    { monacoId: "python",     lspKey: "fastapi",    lspLangId: "python",          ext: "py"   },
  django:     { monacoId: "python",     lspKey: "django",     lspLangId: "python",          ext: "py"   },
  java:       { monacoId: "java",       lspKey: "java",       lspLangId: "java",            ext: "java" },
  springboot: { monacoId: "java",       lspKey: "springboot", lspLangId: "java",            ext: "java" },
  csharp:     { monacoId: "csharp",     lspKey: "csharp",     lspLangId: "csharp",          ext: "cs"   },
  aspnet:     { monacoId: "csharp",     lspKey: "aspnet",     lspLangId: "csharp",          ext: "cs"   },
  typescript: { monacoId: "typescript", lspKey: "typescript", lspLangId: "typescript",      ext: "ts"   },
  react:      { monacoId: "typescript", lspKey: "react",      lspLangId: "typescriptreact", ext: "tsx"  },
  nextjs:     { monacoId: "typescript", lspKey: "nextjs",     lspLangId: "typescriptreact", ext: "tsx"  },
  angular:    { monacoId: "typescript", lspKey: "angular",    lspLangId: "typescript",      ext: "ts"   },
  vue:        { monacoId: "typescript", lspKey: "vue",        lspLangId: "typescript",      ext: "ts"   },
  javascript: { monacoId: "javascript", lspKey: "javascript", lspLangId: "javascript",      ext: "js"   },
  nodejs:     { monacoId: "javascript", lspKey: "nodejs",     lspLangId: "javascript",      ext: "js"   },
  express:    { monacoId: "javascript", lspKey: "express",    lspLangId: "javascript",      ext: "js"   },
  shell:      { monacoId: "shell",      lspKey: "bash",       lspLangId: "shellscript",     ext: "sh"   },
  bash:       { monacoId: "shell",      lspKey: "bash",       lspLangId: "shellscript",     ext: "sh"   },
  css:        { monacoId: "css",        lspKey: "css",        lspLangId: "css",             ext: "css"  },
  scss:       { monacoId: "scss",       lspKey: "css",        lspLangId: "scss",            ext: "scss" },
  less:       { monacoId: "less",       lspKey: "css",        lspLangId: "less",            ext: "less" },
  html:       { monacoId: "html",       lspKey: "html",       lspLangId: "html",            ext: "html" },
  sql:        { monacoId: "sql",        lspKey: "sql",        lspLangId: "sql",             ext: "sql"  },
  mysql:      { monacoId: "sql",        lspKey: "sql",        lspLangId: "sql",             ext: "sql"  },
  pgsql:      { monacoId: "sql",        lspKey: "sql",        lspLangId: "sql",             ext: "sql"  },
  c:          { monacoId: "c",          lspKey: "c",          lspLangId: "c",               ext: "c"    },
  cpp:        { monacoId: "cpp",        lspKey: "cpp",        lspLangId: "cpp",             ext: "cpp"  },
  php:        { monacoId: "php",        lspKey: "php",        lspLangId: "php",             ext: "php"  },
};

const FALLBACK_EXT = { python: "py", javascript: "js", typescript: "ts", java: "java", sql: "sql" };

const LSP_PREAMBLES = {
  react:  "import React from 'react';\n",
  nextjs: "import React from 'react';\n",
};

const DAX_KEYWORDS = [
  "EVALUATE", "DEFINE", "MEASURE", "VAR", "RETURN", "ORDER", "BY",
  "ASC", "DESC", "START", "AT", "COLUMN", "TABLE", "TRUE", "FALSE",
  "NOT", "AND", "OR", "IN",
];

const DAX_FUNCTIONS = [
  // Aggregation
  "SUM", "SUMX", "AVERAGE", "AVERAGEX", "MIN", "MINX", "MAX", "MAXX",
  "COUNT", "COUNTA", "COUNTX", "COUNTAX", "COUNTBLANK", "COUNTROWS",
  "DISTINCTCOUNT", "DISTINCTCOUNTNOBLANK", "PRODUCT", "PRODUCTX",
  // Statistical
  "MEDIAN", "MEDIANX", "PERCENTILE.EXC", "PERCENTILE.INC",
  "PERCENTILEX.EXC", "PERCENTILEX.INC", "STDEV.S", "STDEV.P",
  "STDEVX.S", "STDEVX.P", "VAR.S", "VAR.P", "VARX.S", "VARX.P",
  "RANKX", "RANK.EQ",
  // Filter
  "CALCULATE", "CALCULATETABLE", "FILTER", "ALL", "ALLEXCEPT",
  "ALLNOBLANKROW", "ALLSELECTED", "ALLCROSSFILTERED",
  "REMOVEFILTERS", "KEEPFILTERS", "VALUES", "DISTINCT",
  "HASONEVALUE", "HASONEFILTER", "ISFILTERED", "ISCROSSFILTERED",
  "SELECTEDVALUE", "USERELATIONSHIP", "CROSSFILTER",
  // Time intelligence
  "DATEADD", "DATESBETWEEN", "DATESINPERIOD", "DATESYTD", "DATESQTD",
  "DATESMTD", "TOTALYTD", "TOTALQTD", "TOTALMTD",
  "SAMEPERIODLASTYEAR", "PREVIOUSDAY", "PREVIOUSMONTH", "PREVIOUSQUARTER",
  "PREVIOUSYEAR", "NEXTDAY", "NEXTMONTH", "NEXTQUARTER", "NEXTYEAR",
  "STARTOFMONTH", "STARTOFQUARTER", "STARTOFYEAR",
  "ENDOFMONTH", "ENDOFQUARTER", "ENDOFYEAR",
  "FIRSTDATE", "LASTDATE", "FIRSTNONBLANK", "LASTNONBLANK",
  "OPENINGBALANCEMONTH", "OPENINGBALANCEQUARTER", "OPENINGBALANCEYEAR",
  "CLOSINGBALANCEMONTH", "CLOSINGBALANCEQUARTER", "CLOSINGBALANCEYEAR",
  "PARALLELPERIOD", "CALENDAR", "CALENDARAUTO",
  // Date & time
  "DATE", "DATEVALUE", "YEAR", "MONTH", "DAY", "HOUR", "MINUTE", "SECOND",
  "NOW", "TODAY", "UTCNOW", "UTCTODAY", "WEEKDAY", "WEEKNUM",
  "EDATE", "EOMONTH", "DATEDIFF", "FORMAT",
  // Logical
  "IF", "IF.EAGER", "IFERROR", "SWITCH", "AND", "OR", "NOT",
  "TRUE", "FALSE", "BLANK", "ISBLANK", "ISNUMBER", "ISTEXT",
  "ISLOGICAL", "ISERROR", "ISNONTEXT", "ISINSCOPE", "ISEMPTY",
  "COALESCE",
  // Text
  "CONCATENATE", "CONCATENATEX", "LEFT", "RIGHT", "MID", "LEN",
  "UPPER", "LOWER", "TRIM", "SUBSTITUTE", "REPLACE", "REPT",
  "FIND", "SEARCH", "FIXED", "EXACT", "UNICHAR", "UNICODE",
  "COMBINEVALUES", "CONTAINSSTRING", "CONTAINSSTRINGEXACT",
  // Math
  "ABS", "CEILING", "FLOOR", "ROUND", "ROUNDUP", "ROUNDDOWN",
  "TRUNC", "INT", "MOD", "POWER", "SQRT", "SIGN", "EXP", "LN",
  "LOG", "LOG10", "PI", "RAND", "RANDBETWEEN", "DIVIDE",
  "QUOTIENT", "GCD", "LCM", "EVEN", "ODD", "FACT",
  "MROUND", "ISO.CEILING", "CURRENCY", "CONVERT",
  // Table manipulation
  "ADDCOLUMNS", "SELECTCOLUMNS", "SUMMARIZE", "SUMMARIZECOLUMNS",
  "GROUPBY", "CROSSJOIN", "EXCEPT", "INTERSECT", "UNION",
  "NATURALINNERJOIN", "NATURALLEFTOUTERJOIN",
  "TREATAS", "GENERATESERIES", "GENERATE", "GENERATEALL",
  "ROW", "DATATABLE", "TOPN", "SAMPLE",
  "ADDMISSINGITEMS", "ROLLUPADDISSUBTOTAL", "ROLLUPISSUBTOTAL",
  "ROLLUP", "ROLLUPGROUP", "IGNORE",
  // Relationship & navigation
  "RELATED", "RELATEDTABLE", "LOOKUPVALUE", "PATHCONTAINS",
  "PATH", "PATHITEM", "PATHITEMREVERSE", "PATHLENGTH",
  // Information
  "USERPRINCIPALNAME", "USERNAME", "CUSTOMDATA",
  "ISAFTER", "CONTAINS", "CONTAINSROW",
  "COLUMNSTATISTICS", "NAMEOF",
  // Other
  "ERROR", "SELECTEDMEASURE", "SELECTEDMEASURENAME",
  "SELECTEDMEASUREFORMATSTRING",
];

const CodeEditor = ({ language = "javascript", languageVersion, value, onChange, autoCompleteWords, evaluationId, height = "400px" }) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const providerDisposablesRef = useRef([]);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const lspSocketRef = useRef(null);
  const lspModelSyncCleanupRef = useRef(null);
  const lspCompletionProviderRef = useRef(null);

  const normalizedLanguage = language.toLowerCase();
  const lspConfig = LSP_LANG_CONFIG[normalizedLanguage];
  const monacoLanguageId = lspConfig?.monacoId || normalizedLanguage;
  const fileExtension = lspConfig?.ext || FALLBACK_EXT[normalizedLanguage] || "txt";
  const modelPath = `file:///workspace/main.${fileExtension}`;

  // Register question-specific autocomplete words (table names, column names, etc.)
  useEffect(() => {
    if (!isEditorReady) return undefined;

    const extraWords = Array.isArray(autoCompleteWords)
      ? autoCompleteWords.filter((w) => typeof w === "string" && w.trim() !== "")
      : [];
    if (extraWords.length === 0) return undefined;

    const monacoInstance = monacoRef.current;
    const editor = editorRef.current;
    if (!monacoInstance || !editor) return undefined;

    const disposable = monacoInstance.languages.registerCompletionItemProvider(monacoLanguageId, {
      triggerCharacters: [" ", "."],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };
        return {
          suggestions: extraWords.map((label) => ({
            label,
            kind: monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: label,
            range
          }))
        };
      }
    });

    providerDisposablesRef.current.push(disposable);
    return () => {
      disposable.dispose();
      providerDisposablesRef.current = providerDisposablesRef.current.filter((d) => d !== disposable);
    };
  }, [isEditorReady, monacoLanguageId, autoCompleteWords]);

  // ── DAX dictionary completions (no LSP backend for DAX) ───────────────
  useEffect(() => {
    if (!isEditorReady) return undefined;
    if (normalizedLanguage !== "msdax") return undefined;

    const monacoInstance = monacoRef.current;
    const editor = editorRef.current;
    if (!monacoInstance || !editor) return undefined;

    const model = editor.getModel();
    if (model && model.getLanguageId() !== "msdax") {
      monacoInstance.editor.setModelLanguage(model, "msdax");
    }

    const allDaxSuggestions = [...DAX_KEYWORDS, ...DAX_FUNCTIONS];

    const completionDisposable = monacoInstance.languages.registerCompletionItemProvider("msdax", {
      triggerCharacters: [" ", "(", ","],
      provideCompletionItems: (mdl, position) => {
        const word = mdl.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        return {
          suggestions: allDaxSuggestions.map((label) => ({
            label,
            kind: DAX_FUNCTIONS.includes(label)
              ? monacoInstance.languages.CompletionItemKind.Function
              : monacoInstance.languages.CompletionItemKind.Keyword,
            insertText: DAX_FUNCTIONS.includes(label) ? `${label}($0)` : label,
            insertTextRules: DAX_FUNCTIONS.includes(label)
              ? monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet
              : monacoInstance.languages.CompletionItemInsertTextRule.None,
            range,
          })),
        };
      },
    });

    const contentDisposable = editor.onDidChangeModelContent((event) => {
      const hasLetterInsert = event.changes.some((change) =>
        /[a-zA-Z]$/.test(change.text)
      );
      if (hasLetterInsert) {
        editor.trigger("dax-suggest", "editor.action.triggerSuggest", {});
      }
    });

    return () => {
      completionDisposable.dispose();
      contentDisposable.dispose();
    };
  }, [isEditorReady, normalizedLanguage]);

  // ── LSP via raw WebSocket ────────────────────────────────────────────
  // Implements the full LSP lifecycle directly over plain JSON text frames
  // which is exactly what the backend gateway expects.
  useEffect(() => {
    if (!isEditorReady) return undefined;
    if (!lspConfig) return undefined;
    if (!evaluationId) return undefined;

    const monacoInstance = monacoRef.current;
    const editor = editorRef.current;
    if (!monacoInstance || !editor) return undefined;

    const { monacoId, lspKey, lspLangId } = lspConfig;
    const isJsTs = monacoId === "javascript" || monacoId === "typescript";
    const preamble = LSP_PREAMBLES[lspKey] || "";
    const preambleLineCount = preamble ? preamble.split('\n').length - 1 : 0;

    const model = editor.getModel();
    if (model && model.getLanguageId() !== monacoId) {
      monacoInstance.editor.setModelLanguage(model, monacoId);
    }

    // Disable built-in TS/JS workers when an LSP handles JS/TS languages
    // to avoid duplicate completions and diagnostics.
    if (isJsTs && monacoInstance.languages.typescript) {
      const noValidation = { noSemanticValidation: true, noSyntaxValidation: true, noSuggestionDiagnostics: true };
      monacoInstance.languages.typescript.typescriptDefaults?.setDiagnosticsOptions(noValidation);
      monacoInstance.languages.typescript.javascriptDefaults?.setDiagnosticsOptions(noValidation);
    }

    // Tear down any previous session
    if (lspModelSyncCleanupRef.current) {
      lspModelSyncCleanupRef.current();
      lspModelSyncCleanupRef.current = null;
    }
    if (lspCompletionProviderRef.current) {
      lspCompletionProviderRef.current.dispose();
      lspCompletionProviderRef.current = null;
    }
    if (lspSocketRef.current) {
      lspSocketRef.current.close();
      lspSocketRef.current = null;
    }

    // ── Internal state for this LSP session ──
    let nextRequestId = 1;
    const pendingRequests = new Map();
    let lspReady = false;
    let disposed = false;
    let userInitiatedClose = false;
    let reconnectAttempt = 0;
    let reconnectTimer = null;
    let ws = null;
    const MAX_RECONNECT_ATTEMPTS = 8;
    const BASE_RECONNECT_DELAY_MS = 500;
    const MAX_RECONNECT_DELAY_MS = 10000;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const closeSocket = () => {
      if (!ws) return;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        userInitiatedClose = true;
        ws.close();
      }
      ws = null;
      lspSocketRef.current = null;
    };

    const rejectPending = (reason) => {
      for (const [, pending] of pendingRequests) {
        clearTimeout(pending.timer);
        pending.reject(new Error(reason));
      }
      pendingRequests.clear();
    };

    const send = (msg) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        const raw = JSON.stringify(msg);
        if (process.env.NODE_ENV === "development") {
          console.debug("LSP →", msg.method || `response id:${msg.id}`, msg);
        }
        ws.send(raw);
      }
    };

    const sendRequest = (method, params, timeoutMs = 10000) =>
      new Promise((resolve, reject) => {
        if (disposed || !ws || ws.readyState !== WebSocket.OPEN) {
          return reject(new Error("LSP: not connected"));
        }
        const id = nextRequestId++;
        const timer = setTimeout(() => {
          pendingRequests.delete(id);
          reject(new Error(`LSP: ${method} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
        pendingRequests.set(id, { resolve, reject, timer });
        send({ jsonrpc: "2.0", id, method, params });
      });

    const sendNotification = (method, params) => {
      send({ jsonrpc: "2.0", method, params });
    };

    const initializeLspSession = async () => {
      try {
        const activeModel = editor.getModel();
        if (!activeModel || disposed) return;
        const uri = activeModel.uri.toString();

        // 1. initialize
        const initResult = await sendRequest("initialize", {
          processId: null,
          rootUri: "file:///workspace",
          capabilities: {
            textDocument: {
              synchronization: {
                dynamicRegistration: false,
                willSave: false,
                didSave: false,
                willSaveWaitUntil: false
              },
              completion: {
                dynamicRegistration: false,
                completionItem: {
                  snippetSupport: true,
                  commitCharactersSupport: true,
                  documentationFormat: ["plaintext", "markdown"],
                  deprecatedSupport: true,
                  preselectSupport: true
                },
                contextSupport: true
              },
              hover: {
                dynamicRegistration: false,
                contentFormat: ["plaintext", "markdown"]
              }
            },
            workspace: {
              didChangeConfiguration: { dynamicRegistration: false }
            }
          },
          initializationOptions: {}
        }, 15000);

        if (disposed) return;
        console.debug("LSP: server capabilities", initResult);

        // 2. initialized notification
        sendNotification("initialized", {});
        lspReady = true;

        // 3. Open the document
        sendNotification("textDocument/didOpen", {
          textDocument: {
            uri,
            languageId: lspLangId,
            version: activeModel.getVersionId(),
            text: preamble + activeModel.getValue()
          }
        });

        // 4. Sync subsequent edits (full-text mode as backend requires)
        const changeDisposable = activeModel.onDidChangeContent(() => {
          if (!lspReady || disposed) return;
          sendNotification("textDocument/didChange", {
            textDocument: { uri, version: activeModel.getVersionId() },
            contentChanges: [{ text: preamble + activeModel.getValue() }]
          });
        });

        lspModelSyncCleanupRef.current = () => {
          changeDisposable.dispose();
          try {
            sendNotification("textDocument/didClose", { textDocument: { uri } });
          } catch (_) {
            // socket may already be closed
          }
        };

        // 5. Register completion provider using trigger characters advertised by the server
        const serverTriggers = initResult?.capabilities?.completionProvider?.triggerCharacters;
        const fallbackTriggers = [".", "'", "\"", "(", "["];
        const triggerChars = Array.isArray(serverTriggers) && serverTriggers.length > 0
          ? [...new Set([...serverTriggers, ...fallbackTriggers])]
          : fallbackTriggers;

        if (lspCompletionProviderRef.current) {
          lspCompletionProviderRef.current.dispose();
          lspCompletionProviderRef.current = null;
        }

        lspCompletionProviderRef.current = monacoInstance.languages.registerCompletionItemProvider(monacoId, {
          triggerCharacters: triggerChars,
          provideCompletionItems: async (mdl, position, context) => {
            if (!lspReady || disposed || !ws || ws.readyState !== WebSocket.OPEN) {
              return { suggestions: [] };
            }

            const word = mdl.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn
            };

            const triggerKind =
              context?.triggerKind === monacoInstance.languages.CompletionTriggerKind.TriggerCharacter
                ? 2
                : 1;

            try {
              const startedAt = Date.now();
              const response = await sendRequest("textDocument/completion", {
                textDocument: { uri: mdl.uri.toString() },
                position: {
                  line: position.lineNumber - 1 + preambleLineCount,
                  character: position.column - 1
                },
                context: {
                  triggerKind,
                  triggerCharacter:
                    triggerKind === 2 && typeof context?.triggerCharacter === "string"
                      ? context.triggerCharacter
                      : undefined
                }
              }, 10000);

              const items = Array.isArray(response) ? response : (response?.items || []);
              console.debug(
                `LSP: completion in ${Date.now() - startedAt}ms — ${items.length} items`
              );

              const suggestions = items
                .filter((item) => item && (typeof item.label === "string" || typeof item.label?.label === "string"))
                .map((item) => {
                  const label = typeof item.label === "string" ? item.label : item.label.label;
                  return {
                    label,
                    kind: item.kind || monacoInstance.languages.CompletionItemKind.Text,
                    insertText: item.insertText || label,
                    insertTextRules:
                      item.insertTextFormat === 2
                        ? monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet
                        : monacoInstance.languages.CompletionItemInsertTextRule.None,
                    detail: item.detail,
                    documentation:
                      typeof item.documentation === "string"
                        ? item.documentation
                        : item.documentation?.value,
                    range
                  };
                });

              return { suggestions };
            } catch (err) {
              console.warn("LSP: completion failed", err?.message || err);
              return { suggestions: [] };
            }
          }
        });
      } catch (err) {
        console.error("LSP: initialization failed", err);
      }
    };

    const scheduleReconnect = () => {
      if (disposed || userInitiatedClose) return;
      if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
        console.error("LSP: max reconnect attempts reached");
        return;
      }
      const delay = Math.min(BASE_RECONNECT_DELAY_MS * (2 ** reconnectAttempt), MAX_RECONNECT_DELAY_MS);
      reconnectAttempt += 1;
      clearReconnectTimer();
      reconnectTimer = setTimeout(() => {
        connectWithFreshTicket();
      }, delay);
    };

    const connectWithFreshTicket = async () => {
      if (disposed) return;

      try {
        const wsBase = getWsBaseUrl();
        if (!wsBase) {
          throw new Error("LSP: API/WS base URL not configured");
        }
        const ticket = await createWsTicket(evaluationId, "lsp");
        if (disposed) return;

        const socketUrl = new URL(`${wsBase}/lsp`);
        socketUrl.searchParams.set("language", lspKey);
        if (languageVersion) {
          socketUrl.searchParams.set("languageVersion", languageVersion);
        }
        socketUrl.searchParams.set("ws_token", ticket.ws_token);

        closeSocket();
        userInitiatedClose = false;

        ws = new WebSocket(socketUrl.toString());
        lspSocketRef.current = ws;

        ws.addEventListener("message", (event) => {
          let msg;
          try {
            msg = JSON.parse(event.data);
          } catch {
            return;
          }
          if (process.env.NODE_ENV === "development") {
            console.debug("LSP ←", msg.method || `response id:${msg.id}`, msg);
          }

          if (
            msg.id !== undefined &&
            msg.id !== null &&
            !msg.method &&
            (msg.result !== undefined || msg.error !== undefined)
          ) {
            const pending = pendingRequests.get(msg.id);
            if (pending) {
              pendingRequests.delete(msg.id);
              clearTimeout(pending.timer);
              if (msg.error) {
                pending.reject(msg.error);
              } else {
                pending.resolve(msg.result);
              }
            }
            return;
          }

          if (msg.id !== undefined && msg.method) {
            send({ jsonrpc: "2.0", id: msg.id, result: null });
            return;
          }

          if (msg.error && !msg.id && !msg.method) {
            console.error("LSP: server error payload", msg.error);
          }
        });

        ws.addEventListener("open", async () => {
          reconnectAttempt = 0;
          await initializeLspSession();
        });

        ws.addEventListener("close", (event) => {
          console.debug("LSP: WebSocket closed", event.code, event.reason);
          lspReady = false;
          rejectPending("WebSocket closed");

          if (lspModelSyncCleanupRef.current) {
            lspModelSyncCleanupRef.current();
            lspModelSyncCleanupRef.current = null;
          }
          if (!disposed && !userInitiatedClose) {
            scheduleReconnect();
          }
        });

        ws.addEventListener("error", (event) => {
          console.error("LSP: WebSocket error", event);
        });
      } catch (err) {
        console.error("LSP: failed to create session", err);
        scheduleReconnect();
      }
    };

    connectWithFreshTicket();

    // ── Effect cleanup ──
    return () => {
      disposed = true;
      lspReady = false;
      clearReconnectTimer();
      if (lspModelSyncCleanupRef.current) {
        lspModelSyncCleanupRef.current();
        lspModelSyncCleanupRef.current = null;
      }
      if (lspCompletionProviderRef.current) {
        lspCompletionProviderRef.current.dispose();
        lspCompletionProviderRef.current = null;
      }
      rejectPending("disposed");
      userInitiatedClose = true;
      closeSocket();

      // Re-enable built-in TS/JS workers when leaving a JS/TS LSP session
      if (isJsTs && monacoInstance.languages.typescript) {
        const reEnable = { noSemanticValidation: false, noSyntaxValidation: false, noSuggestionDiagnostics: false };
        monacoInstance.languages.typescript.typescriptDefaults?.setDiagnosticsOptions(reEnable);
        monacoInstance.languages.typescript.javascriptDefaults?.setDiagnosticsOptions(reEnable);
      }
    };
  }, [evaluationId, isEditorReady, lspConfig, languageVersion]);
  
  return (
    <div className="code-editor" style={{ border: "1px solid #e0e0e0", borderRadius: 8, height: height, display: "flex", flexDirection: "column" }}>
      <Editor
        height="100%"
        language={monacoLanguageId}
        path={modelPath}
        theme="vs-dark"
        value={value}
        onChange={(val) => onChange(val || "")}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          padding: { top: 10 },
          // Render suggest/hover widgets outside the editor's overflow container
          // so they are never clipped by the editor boundary.
          fixedOverflowWidgets: true,
          // Prevent copy-paste in the editor
          copyWithSyntaxHighlighting: false,
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          acceptSuggestionOnCommitCharacter: true,
          acceptSuggestionOnEnter: "on",
          tabCompletion: "on",
          wordBasedSuggestions: true,
          parameterHints: { enabled: true },
          suggest: { enabled: true },
          // Disable context menu (right-click menu)
          contextmenu: false,
          // Disable find and replace
          find: { addExtraSpaceOnTop: false },
          // Disable keyboard shortcuts for copy/paste
          quickAccess: { enabled: false },
        }}
        onMount={(editor, monacoInstance) => {
          editorRef.current = editor;
          monacoRef.current = monacoInstance;
          setIsEditorReady(true);
          // Disable copy-paste keyboard shortcuts
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyC, () => {});
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => {});
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyX, () => {});
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyA, () => {});
          
          // Disable right-click context menu
          editor.onContextMenu(() => {
            return false;
          });
          
          // Prevent drag and drop
          // Note: Monaco Editor doesn't have onDidDropTextEditor method
          // Drag and drop is controlled via the options above
        }}
      />
    </div>
  );
};

export default CodeEditor; 